import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Settings, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { TrackingLink } from '@/lib/marketing';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ui/theme-toggle";

/**
 * One row inside a nav dropdown, on desktop and in the mobile flat list alike.
 *
 * `description` and `separatorBefore` exist for the PRODUCT MENUS introduced by
 * the 2026-09-11 nav redesign ("Nav Design 3a/3b"): the root nav's Law School
 * and Bar Prep menus list PRODUCTS with a price or availability line, and put a
 * rule between the products and the non-product links beneath them. Both are
 * optional, so the older anchor-list dropdowns are unchanged.
 */
export interface NavDropdownItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
  /** Second line under the label — a price, an availability date, a one-liner. */
  description?: string;
  /** Draw a divider above this row. */
  separatorBefore?: boolean;
}

interface NavItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  isActive?: boolean;
  customElement?: React.ReactNode;
  mobileChildren?: NavDropdownItem[];
}

/** A small icon-only link in the right-hand utility cluster (e.g. YouTube). */
export interface NavUtilityLink {
  href: string;
  /** Accessible name — the link renders as an icon, so this is the only label. */
  label: string;
  icon: React.ReactNode;
}

interface NavbarProps {
  siteName: 'jds' | 'askjds';
  navItems: NavItem[];
  showPromo?: boolean;
  logoSize?: 'sm' | 'md' | 'lg';
  className?: string;
  dashboardText?: string;
  dashboardHref?: string;
  /**
   * Product-context label rendered after the logo, behind a vertical rule
   * ("Law School" on /law-school/*, "Bar Prep" on /bar/*).
   *
   * AN OPTIONAL PROP, NOT A FORK — the designer said so in as many words. Three
   * marketing navbars share this shell; giving the context navbars their own
   * copy of it is how the four hand-maintained nav implementations this repo
   * already has came to exist.
   */
  contextLabel?: string;
  /** Ink for `contextLabel`. Law school is jdblue; bar is brand orange. */
  contextColor?: 'jdblue' | 'orange';
  /** Icon-only links in the right-hand cluster, left of the theme toggle. */
  utilityLinks?: NavUtilityLink[];
}

const CONTEXT_INK: Record<'jdblue' | 'orange', string> = {
  jdblue: 'text-[#00178E] dark:text-[#5b74e8]',
  orange: 'text-[#F37022]',
};

export function Navbar({
  siteName = 'jds',
  navItems,
  showPromo = false,
  logoSize = 'md',
  className,
  dashboardText = siteName === 'jds' ? 'Dashboard' : 'Chat',
  dashboardHref = siteName === 'jds' ? '/courses' : '/chat',
  contextLabel,
  contextColor = 'jdblue',
  utilityLinks,
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();
  
  // Close mobile menu on route change (pathname or hash)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.hash]);
  
  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      window.location.href = '/';
    }
  };
  
  // Trailing-slash-tolerant path match. An exact compare makes `/law-school/`
  // miss `/law-school`, which silently un-highlights the nav item — no error,
  // just a navbar that looks unvisited. Same defect was live in
  // NextGenPrepNavbar's isOnPrepPage, and was fixed there too before that
  // component was retired with the /nextgen-prep 301; MarketingBarNavbar
  // inherited the repaired form (.claude/rules/propagate-the-fix.md — these have
  // no vendoring relationship, so check-shared-drift.sh cannot see the pair).
  const samePath = (a: string, b: string) => {
    const strip = (p: string) => p.replace(/\/+$/, '') || '/';
    return strip(a) === strip(b);
  };

  // Determine logo size class
  const logoSizeClass = {
    sm: 'h-8',
    md: 'h-10',
    lg: 'h-12'
  }[logoSize];
  
  return (
    <nav
      className={cn(
        "fixed w-full z-40",
        "glass dark:glass-dark",
        // Push navbar content below the iOS status bar / Dynamic Island on the
        // native build. env(safe-area-inset-top) is 0 on web, so this is a
        // no-op in the browser (web stays pixel-identical).
        "pt-safe-top",
        showPromo && "top-10",
        className
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <TrackingLink href="/" campaign="navbar" content="logo" className="transition-opacity hover:opacity-90">
              <img
                data-twic-src="media:images/logo/jd-simplified-logo.png?v=20260530"
                data-twic-transform="quality=95/output=auto"
                data-twic-intrinsic="1280x512"
                alt="JD Simplified Logo"
                className={`${logoSizeClass} dark:hidden`}
              />
              <img
                data-twic-src="media:images/logo/jd-simplified-logo-dark.png?v=20260530"
                data-twic-transform="quality=95/output=auto"
                alt="JD Simplified Logo"
                className={`${logoSizeClass} hidden dark:block`}
              />
            </TrackingLink>

            {/* Product-context label: logo | divider | label. Hidden below `sm`
                so it never competes with the hamburger on a 390px screen — the
                mobile menu carries the same items and the page below says which
                product you are on. */}
            {contextLabel && (
              <>
                <span
                  aria-hidden="true"
                  className="hidden sm:block ml-3 mr-3 h-6 w-px bg-gray-300 dark:bg-gray-600"
                />
                <span
                  className={cn(
                    'hidden sm:block text-sm font-bold uppercase tracking-[0.06em]',
                    CONTEXT_INK[contextColor]
                  )}
                >
                  {contextLabel}
                </span>
              </>
            )}
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex md:flex-1 md:justify-center md:items-center">
            <div className="flex items-center space-x-4">
              {navItems.map((item) => {
                const isActiveItem = item.isActive || samePath(location.pathname, item.href);
                const isExternal = item.href.startsWith('http');

                // If custom element is provided, render it instead of Link
                if (item.customElement) {
                  return <div key={item.label}>{item.customElement}</div>;
                }

                // External links use <a> tag
                if (isExternal) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className={cn(
                        siteName === 'askjds' ? "nav-link-askjds" : "nav-link",
                        isActiveItem && siteName === 'askjds' && "text-[#F37022] after:w-full",
                        isActiveItem && siteName !== 'askjds' && "text-jdblue after:w-full"
                      )}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </a>
                  );
                }

                // Internal links use React Router Link
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      siteName === 'askjds' ? "nav-link-askjds" : "nav-link",
                      isActiveItem && siteName === 'askjds' && "text-[#F37022] after:w-full",
                      isActiveItem && siteName !== 'askjds' && "text-jdblue after:w-full"
                    )}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
          
          {/* Auth Section */}
          <div className="hidden md:flex md:items-center">
            {/* Utility icons (YouTube). ICONS, NOT WORDS, and that is a founder
                decision rather than a space saving: an outbound text slot sends
                qualified traffic off-site from the one position on the page
                where buying happens. */}
            {utilityLinks?.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.label}
                title={link.label}
                className="mr-1 inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-600 transition-colors hover:text-[#F37022] dark:text-gray-300 dark:hover:text-[#F37022]"
              >
                {link.icon}
              </a>
            ))}
            <ThemeToggle className="mr-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" />
            {user ? (
              <div className="flex items-center space-x-4">
                <TrackingLink href={dashboardHref} campaign="navbar" content="dashboard">
                  <Button variant="default" className={cn(
                    siteName === 'askjds'
                      ? "bg-jdorange hover:bg-jdorange-dark"
                      : "bg-jdblue hover:bg-jdblue-light"
                  )}>
                    {dashboardText}
                  </Button>
                </TrackingLink>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-full w-10 h-10 p-0 ml-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white">
                      <User className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                      {user?.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <TrackingLink href="/settings" campaign="navbar" content="settings">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </TrackingLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Sign out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <TrackingLink href="/login" campaign="navbar" content="login">
                  <Button variant="ghost">Log In</Button>
                </TrackingLink>
                {/* SIGN UP IS ORANGE UNIVERSALLY — founder decision 2026-09-11,
                    settling the open question the designer raised ("orange on the
                    root and blue in the law school context, or one color
                    everywhere"). It used to key off siteName, which painted it
                    jdblue for siteName="jds". One conversion affordance, one
                    colour, on every marketing surface. The DASHBOARD/Access button
                    below keeps the siteName split — it is not a signup CTA. */}
                <TrackingLink href="/auth?tab=signup" campaign="navbar" content="sign_up">
                  <Button variant="default" className="bg-jdorange hover:bg-jdorange-dark">
                    Sign Up
                  </Button>
                </TrackingLink>
              </div>
            )}
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 dark:text-gray-300 hover:text-jdblue"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden">
          <div className={cn(
            "px-2 pt-2 pb-3 space-y-1 border-b dark:border-gray-700",
            "bg-white dark:bg-gray-900"
          )}>
            {navItems.map((item) => {
              const isActiveItem = item.isActive || samePath(location.pathname, item.href);
              const isExternal = item.href.startsWith('http');

              // If mobileChildren is provided, render as expandable section (for Features)
              if (item.mobileChildren) {
                return (
                  <div key={item.label} className="space-y-1">
                    <div className={cn(
                      "flex items-center px-3 py-2 rounded-md text-base font-medium",
                      isActiveItem && siteName === 'askjds' && "text-[#F37022]",
                      isActiveItem && siteName !== 'askjds' && "text-jdblue",
                      !isActiveItem && "text-gray-600 dark:text-gray-400"
                    )}>
                      {item.label}
                    </div>
                    {/* Render mobile children as flat list */}
                    <div className="pl-6 space-y-1">
                      {item.mobileChildren.map((child) => {
                        // ROUTE vs IN-PAGE ANCHOR — two different elements, and
                        // the difference is a full page reload.
                        //
                        // These were ALL plain `<a href>` with an unconditional
                        // preventDefault() and a getElementById on
                        // `href.split('#')[1]`. For a ROUTE href like
                        // "/law-school" that hash is `undefined`, the lookup
                        // returns null, the branch does nothing, and because the
                        // default had already been prevented the link was simply
                        // DEAD: no navigation, no error, no console warning.
                        //
                        // The first repair made the handler conditional, which
                        // fixed the dead link — and left a plain `<a>` doing a
                        // FULL DOCUMENT LOAD for every product-menu item on
                        // mobile: the whole SPA bundle re-downloaded, auth
                        // context rebuilt, scroll position lost. Working, and
                        // wrong in a way only a phone shows you. The desktop
                        // menu (MarketingNavDropdown) already used <Link>.
                        //
                        // So: a route child is a react-router <Link>; an in-page
                        // anchor stays an <a> with preventDefault + smooth
                        // scroll, which is the one case where the browser
                        // default is the wrong behaviour.
                        const [childPath, childHash] = child.href.split('#');
                        const isInPage =
                          Boolean(childHash) &&
                          (childPath === '' || samePath(location.pathname, childPath));
                        const isExternalChild = child.href.startsWith('http');

                        const body = (
                          <>
                            {child.icon}
                            <span className="flex flex-col">
                              <span>{child.label}</span>
                              {child.description && (
                                <span className="text-xs font-normal text-gray-500 dark:text-gray-500">
                                  {child.description}
                                </span>
                              )}
                            </span>
                          </>
                        );

                        const childClass =
                          'flex min-h-[44px] items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-[#F37022]';

                        return (
                          <div key={child.label}>
                            {child.separatorBefore && (
                              <div
                                aria-hidden="true"
                                className="my-1 ml-3 mr-3 h-px bg-gray-200 dark:bg-gray-700"
                              />
                            )}
                            {isInPage || isExternalChild ? (
                              <a
                                href={child.href}
                                {...(isExternalChild
                                  ? { target: '_blank', rel: 'noopener noreferrer' }
                                  : {})}
                                className={childClass}
                                onClick={(e) => {
                                  if (isInPage) {
                                    const element = document.getElementById(childHash);
                                    if (element) {
                                      e.preventDefault();
                                      element.scrollIntoView({ behavior: 'smooth' });
                                    }
                                  }
                                  // Close the menu in both branches: a real
                                  // navigation unmounts it anyway, and closing
                                  // first avoids it flashing over the
                                  // destination.
                                  setIsOpen(false);
                                }}
                              >
                                {body}
                              </a>
                            ) : (
                              <Link
                                to={child.href}
                                className={childClass}
                                onClick={() => setIsOpen(false)}
                              >
                                {body}
                              </Link>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // If custom element is provided for mobile, render it
              if (item.customElement) {
                return <div key={item.label} className="px-3 py-2">{item.customElement}</div>;
              }

              // External links use <a> tag
              if (isExternal) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800",
                      isActiveItem && siteName === 'askjds' && "text-[#F37022] hover:text-[#F37022]",
                      isActiveItem && siteName !== 'askjds' && "text-jdblue hover:text-jdblue",
                      isActiveItem && "bg-gray-50 dark:bg-gray-800"
                    )}
                    onClick={(e) => {
                      if (item.onClick) item.onClick(e);
                      setIsOpen(false);
                    }}
                  >
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    {item.label}
                  </a>
                );
              }

              // Internal links use React Router Link
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800",
                    isActiveItem && siteName === 'askjds' && "text-[#F37022] hover:text-[#F37022]",
                    isActiveItem && siteName !== 'askjds' && "text-jdblue hover:text-jdblue",
                    isActiveItem && "bg-gray-50 dark:bg-gray-800"
                  )}
                  onClick={(e) => {
                    if (item.onClick) item.onClick(e);
                    setIsOpen(false);
                  }}
                >
                  {item.icon && <span className="mr-2">{item.icon}</span>}
                  {item.label}
                </Link>
              );
            })}
            
            {/* Utility links (YouTube). Labelled here rather than icon-only:
                the desktop cluster has hover/title affordances a touch screen
                does not, and there is no horizontal-space argument in a stacked
                menu. */}
            {utilityLinks?.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-[44px] items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-[#F37022]"
                onClick={() => setIsOpen(false)}
              >
                <span className="mr-2">{link.icon}</span>
                {link.label}
              </a>
            ))}

            {/* Theme Toggle */}
            <div className="px-3 py-2">
              <ThemeToggle variant="switch" />
            </div>

            {/* Mobile Auth Section */}
            {user ? (
              <>
                <TrackingLink
                  href={dashboardHref}
                  campaign="navbar"
                  content="mobile_dashboard"
                  className={cn(
                    "flex items-center px-3 py-2 rounded-md text-base font-medium text-white hover:bg-opacity-90",
                    siteName === 'askjds'
                      ? "bg-jdorange hover:bg-jdorange/90"
                      : "bg-jdblue hover:bg-jdblue/90"
                  )}
                >
                  {dashboardText}
                </TrackingLink>
                <TrackingLink
                  href="/settings"
                  campaign="navbar"
                  content="mobile_settings"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:text-jdblue hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </TrackingLink>
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:text-jdblue hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </button>
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-500">
                  Signed in as: {user.email}
                </div>
              </>
            ) : (
              <>
                <TrackingLink
                  href="/login"
                  campaign="navbar"
                  content="mobile_login"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:text-jdblue hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Log In
                </TrackingLink>
                <TrackingLink
                  href="/auth?tab=signup"
                  campaign="navbar"
                  content="mobile_sign_up"
                  // Orange universally — see the desktop Sign Up above.
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-white bg-jdorange hover:bg-jdorange/90"
                >
                  Sign Up
                </TrackingLink>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
} 